import { Disclosure, Popover, Transition } from "@headlessui/react";
import React, { useState } from "react";
import { BiWallet } from "react-icons/bi";
import { BsCalendar2Week, BsQuestion } from "react-icons/bs";
import { FiMinus, FiPlus } from "react-icons/fi";
import { HiExclamationTriangle } from "react-icons/hi2";
import { IoFlashOutline } from "react-icons/io5";

import {
  CardRow,
  CardRowActions,
  CardRowCell,
  CardRowCells,
  CardRowDivider,
} from "@/components/CardRow";
import CalculationDetailsModal from "@/components/Modal/CalculationDetailsModal";
import Typography from "@/components/Typography";

import { Container, InformationLinedDiv } from "./styles";
import { BillsInfoProps } from "./types";

const DropDownCard = ({
  month,
  value,
  compensableTariff,
  kilowatts,
  status,
  total,
  was_manually_calculated,
  discountPercentage,
  subRows,
}: BillsInfoProps) => {
  const porcentageGeedee = () => discountPercentage * 100;
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);

  return (
    <Container>
      <Disclosure>
        {({ open }) => (
          <>
            <CardRow
              as={Disclosure.Button}
              className="relative z-[1] !h-[88px] cursor-pointer"
              data-testid={`icon-button-${month}`}
            >
              <CardRowCells>
                <CardRowCell label="REFERÊNCIA" value={month} icon={<BsCalendar2Week />} />
                <CardRowCell label="VALOR" value={total} icon={<BiWallet />} />
                <CardRowCell label="KILOWATTS" value={kilowatts} icon={<IoFlashOutline />} />
              </CardRowCells>
              <CardRowActions>
                <CardRowCell
                  label="Situação"
                  status={{
                    label: status,
                    tone: status === "Pago" || status === "Ativo" ? "success" : "danger",
                  }}
                />
                <CardRowDivider />
                <div className="flex size-10 items-center justify-center rounded-full bg-black text-xl text-white">
                  {!open ? <FiPlus /> : <FiMinus />}
                </div>
              </CardRowActions>
            </CardRow>
            <Transition
              enter="transform transition duration-500 ease-custom"
              enterFrom="-translate-y-1/2 scale-y-0 opacity-0"
              enterTo="translate-y-0 scale-y-100 opacity-100"
              leave="transform transition duration-500 ease-custom"
              leaveFrom="translate-y-0 scale-y-100 opacity-100"
              leaveTo="-translate-y-1/2 scale-y-0 opacity-0"
            >
              <Disclosure.Panel className="drop-down-text">
                <div className="mb-4 flex items-center justify-between">
                  <Typography variant="h6" color="white">
                    Transparência Geedee
                  </Typography>
                  {subRows && subRows.length > 0 && (
                    <button
                      onClick={() => setIsCalculationModalOpen(true)}
                      className="rounded-lg bg-secondary300 px-3 py-1 text-sm font-semibold text-black transition-colors hover:bg-secondary400"
                    >
                      Ver Cálculo
                    </button>
                  )}
                </div>
                <InformationLinedDiv>
                  <Typography variant="btnLarge" color="white">
                    Energia Elétrica Injetada
                  </Typography>
                  <Typography variant="btnLarge" color="white">
                    {kilowatts} KWh
                  </Typography>
                </InformationLinedDiv>
                <InformationLinedDiv>
                  <Typography variant="pLarge" color="white">
                    Tarifa da distribuidora com imposto
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {value} / kWh
                  </Typography>
                </InformationLinedDiv>
                <InformationLinedDiv>
                  <div className="items-left flex flex-col gap-2 lg:flex-row lg:items-center">
                    <Typography variant="pLarge" color="white">
                      Tarifa Compensável
                    </Typography>
                    <Popover className="z-100 relative">
                      <Popover.Button>
                        <BsQuestion
                          className="mt-1 rounded-full bg-white"
                          color="primary"
                          size={22}
                        />
                      </Popover.Button>

                      <Popover.Panel className="z-100 absolute w-64 rounded bg-gray-900 p-5 lg:w-96">
                        <Typography variant="pLarge" color="white">
                          A tarifa compensável é definida como a tarifa da distribuidora subtraída
                          do ICMS da TUSD
                        </Typography>
                      </Popover.Panel>
                    </Popover>
                  </div>

                  {!was_manually_calculated ? (
                    <Typography variant="pLarge" color="white">
                      {compensableTariff} / kWh
                    </Typography>
                  ) : (
                    <Popover className="z-100 relative">
                      <Popover.Button className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold transition-colors">
                        <HiExclamationTriangle size={24} />
                      </Popover.Button>

                      <Popover.Panel className="z-100 absolute right-0 w-80 rounded-lg bg-gray-900 p-5 shadow-xl">
                        <Typography variant="pLarge" color="white">
                          Não foi possível calcular a tarifa compensável para esta fatura, pois ela
                          foi processada manualmente.
                        </Typography>
                      </Popover.Panel>
                    </Popover>
                  )}
                </InformationLinedDiv>
                <InformationLinedDiv>
                  <Typography variant="pLarge" color="white">
                    Desconto (sobre a conta da distribuidora)
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {porcentageGeedee().toFixed(2)} %
                  </Typography>
                </InformationLinedDiv>
                {/* <InformationLinedDiv>
                  <Typography variant="pLarge" color="white">
                    Tarifa Geedee
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {moneyFormatter(totalKwh)} / kWh
                  </Typography>
                </InformationLinedDiv> */}
                <InformationLinedDiv>
                  <Typography variant="btnLarge" color="white">
                    Total
                  </Typography>
                  <Typography variant="btnLarge" color="white">
                    {total}
                  </Typography>
                </InformationLinedDiv>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
      {subRows && subRows.length > 0 && (
        <CalculationDetailsModal
          isOpen={isCalculationModalOpen}
          setIsOpen={setIsCalculationModalOpen}
          subRows={subRows}
          month={month}
          discountPercentage={discountPercentage}
          displayedValue={total}
        />
      )}
    </Container>
  );
};

export default DropDownCard;
